import * as React from 'react';
import { Activity, Lightbulb, Loader2, Play, Plus, Search } from 'lucide-react';
import type { LanguageCode, TtsModel, SttProvider } from '@cp3/shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Slider } from '../../../../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components/ui/select';
import { Skeleton } from '../../../../components/ui/skeleton';
import { Button } from '../../../../components/ui/button';
import {
  useAddSharedVoice,
  useElevenLabsVoices,
  type ElevenLabsVoiceSummary,
  type VoiceSource,
} from '../../../../hooks/useElevenLabsVoices';
import { useEffectiveness, type EffectivenessReport } from '../../../../hooks/useEffectiveness';
import { useToast } from '../../../../components/ui/toast';
import { cn } from '../../../../lib/utils';
import type { FormState } from '../state';

interface Props {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
}

const LANG_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'Alle Sprachen' },
  { value: 'de', label: 'Deutsch' },
  { value: 'en', label: 'Englisch' },
  { value: 'fr', label: 'Französisch' },
  { value: 'es', label: 'Spanisch' },
  { value: 'it', label: 'Italienisch' },
  { value: 'pl', label: 'Polnisch' },
  { value: 'tr', label: 'Türkisch' },
  { value: 'ar', label: 'Arabisch' },
];

export function VoiceTab({ form, setForm }: Props) {
  const [source, setSource] = React.useState<VoiceSource>('personal');
  const [language, setLanguage] = React.useState<string>('');
  const [search, setSearch] = React.useState('');
  const voices = useElevenLabsVoices({ source, language });
  const addShared = useAddSharedVoice();
  const { toast } = useToast();
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = React.useState<string | null>(null);

  const filtered = React.useMemo(() => {
    const list = voices.data ?? [];
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter(
      (v) =>
        v.name.toLowerCase().includes(q) ||
        Object.values(v.labels ?? {}).some((l) => l.toLowerCase().includes(q)),
    );
  }, [voices.data, search]);

  function play(v: ElevenLabsVoiceSummary) {
    if (!v.preview_url) return;
    if (audioRef.current) audioRef.current.pause();
    const audio = new Audio(v.preview_url);
    audioRef.current = audio;
    setPlaying(v.voice_id);
    audio.onended = () => setPlaying(null);
    void audio.play().catch(() => setPlaying(null));
  }

  async function importVoice(v: ElevenLabsVoiceSummary) {
    if (!v.owner_id) return;
    try {
      const newName = window.prompt(
        'Name für die importierte Stimme in deiner Bibliothek:',
        v.name,
      );
      if (!newName) return;
      const newId = await addShared.mutateAsync({
        ownerId: v.owner_id,
        voiceId: v.voice_id,
        newName,
      });
      toast({ title: 'Stimme importiert', description: `Voice-ID: ${newId}` });
      setForm((p) => ({ ...p, ttsVoiceId: newId }));
      setSource('personal');
    } catch (err) {
      toast({
        title: 'Import fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  const isNative = form.voiceAgentMode === 'native';

  return (
    <div className="space-y-6">
      <PerformanceCard />

      <VoiceAgentModeCard form={form} setForm={setForm} />

      <Card className={cn(isNative && 'opacity-60')}>
        <CardHeader>
          <CardTitle>
            Sprache & Pipeline
            {isNative && (
              <span className="ml-2 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-amber-300">
                im Native-Modus inaktiv
              </span>
            )}
          </CardTitle>
          <CardDescription>
            {isNative
              ? 'Im nativen Voice-Agent-Modus übernimmt der gewählte Provider STT + TTS direkt. Diese Einstellungen wirken nur im klassischen Modus.'
              : 'Telefon-Audio ist 8 kHz μ-law — die Pipeline rechnet automatisch um.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Sprache</Label>
              <Select
                value={form.language}
                onValueChange={(v) => setForm((p) => ({ ...p, language: v as LanguageCode }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="de-DE">Deutsch (Deutschland)</SelectItem>
                  <SelectItem value="de-AT">Deutsch (Österreich)</SelectItem>
                  <SelectItem value="en-US">Englisch (US)</SelectItem>
                  <SelectItem value="multi">Mehrsprachig</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>STT-Provider</Label>
              <Select
                value={form.sttProvider}
                onValueChange={(v) => setForm((p) => ({ ...p, sttProvider: v as SttProvider }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="deepgram">Deepgram Nova-3</SelectItem>
                  <SelectItem value="elevenlabs">ElevenLabs Scribe v2</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>TTS-Modell</Label>
              <Select
                value={form.ttsModel}
                onValueChange={(v) => setForm((p) => ({ ...p, ttsModel: v as TtsModel }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="eleven_flash_v2_5">Flash v2.5 (~75 ms TTFB)</SelectItem>
                  <SelectItem value="eleven_multilingual_v2">Multilingual v2 (Qualität)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Stimme</CardTitle>
          <CardDescription>
            Wähle aus deiner Bibliothek oder dem ElevenLabs Community-Pool. Klick auf ▶︎ zum Vorhören,
            ＋ um eine Community-Stimme in deine Bibliothek zu importieren.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {/* Source switcher */}
          <div className="flex flex-wrap items-center gap-2">
            <SourceTab active={source === 'personal'} onClick={() => setSource('personal')}>
              Meine Bibliothek
            </SourceTab>
            <SourceTab active={source === 'community'} onClick={() => setSource('community')}>
              Community
            </SourceTab>
            <SourceTab active={source === 'all'} onClick={() => setSource('all')}>
              Alle
            </SourceTab>
            <div className="ml-auto w-44">
              <Select value={language || '__all'} onValueChange={(v) => setLanguage(v === '__all' ? '' : v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {LANG_OPTIONS.map((o) => (
                    <SelectItem key={o.value || '__all'} value={o.value || '__all'}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input className="pl-9" placeholder="Stimme suchen …" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>

          {voices.isLoading && (
            <div className="flex h-32 items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade Voice-Library …
            </div>
          )}
          {voices.isError && (
            <div className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm">
              Voice-Library konnte nicht geladen werden: {(voices.error as Error).message}
            </div>
          )}

          {voices.data && (
            <div className="grid max-h-[420px] gap-2 overflow-auto sm:grid-cols-2">
              {filtered.map((v) => {
                const isCommunity = v.source === 'community';
                return (
                  <div
                    key={v.voice_id}
                    className={cn(
                      'flex items-center gap-3 rounded-md border p-3 text-left transition-colors',
                      form.ttsVoiceId === v.voice_id
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-secondary',
                    )}
                  >
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={(e) => { e.stopPropagation(); play(v); }}
                      disabled={!v.preview_url}
                      className="h-8 w-8 shrink-0"
                      aria-label={`${v.name} abspielen`}
                    >
                      {playing === v.voice_id ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <Play className="h-3.5 w-3.5" />
                      )}
                    </Button>
                    <button
                      type="button"
                      onClick={() => !isCommunity && setForm((p) => ({ ...p, ttsVoiceId: v.voice_id }))}
                      className="min-w-0 flex-1 text-left disabled:cursor-default"
                      disabled={isCommunity}
                    >
                      <div className="flex items-center gap-1.5 truncate font-medium">
                        {v.name}
                        <SourceBadge value={v.source} />
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {(v.labels?.language ?? v.language ?? v.category ?? '—')}
                        {v.labels?.gender ? ` · ${v.labels.gender}` : ''}
                        {v.labels?.accent ? ` · ${v.labels.accent}` : ''}
                      </div>
                    </button>
                    {isCommunity && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => void importVoice(v)}
                        disabled={addShared.isPending}
                        className="shrink-0"
                      >
                        {addShared.isPending ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <>
                            <Plus className="mr-1 h-3.5 w-3.5" /> Hinzufügen
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div className="col-span-full rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                  Keine Stimme gefunden.
                </div>
              )}
            </div>
          )}

          <div className="space-y-1.5 pt-2">
            <Label>Voice-ID (manuell)</Label>
            <Input value={form.ttsVoiceId} onChange={(e) => setForm((p) => ({ ...p, ttsVoiceId: e.target.value }))} />
          </div>

          <div className="grid gap-4 pt-2 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Stability: {form.ttsStability.toFixed(2)}</Label>
              <Slider value={[form.ttsStability]} min={0} max={1} step={0.05} onValueChange={(v) => setForm((p) => ({ ...p, ttsStability: v[0] ?? p.ttsStability }))} />
            </div>
            <div className="space-y-1.5">
              <Label>Similarity Boost: {form.ttsSimilarityBoost.toFixed(2)}</Label>
              <Slider value={[form.ttsSimilarityBoost]} min={0} max={1} step={0.05} onValueChange={(v) => setForm((p) => ({ ...p, ttsSimilarityBoost: v[0] ?? p.ttsSimilarityBoost }))} />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SourceTab({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary/10 text-primary'
          : 'border-border text-muted-foreground hover:bg-secondary',
      )}
    >
      {children}
    </button>
  );
}

function SourceBadge({ value }: { value?: ElevenLabsVoiceSummary['source'] }) {
  if (!value) return null;
  const map: Record<string, { label: string; cls: string }> = {
    personal: { label: 'Eigene', cls: 'bg-primary/15 text-primary' },
    cloned: { label: 'Klon', cls: 'bg-emerald-500/15 text-emerald-400' },
    workspace: { label: 'Workspace', cls: 'bg-amber-500/15 text-amber-400' },
    community: { label: 'Community', cls: 'bg-sky-500/15 text-sky-400' },
  };
  const m = map[value] ?? { label: value, cls: 'bg-muted text-muted-foreground' };
  return (
    <span className={cn('rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-wider', m.cls)}>
      {m.label}
    </span>
  );
}

// ── Voice-Agent Mode (classic vs. native speech-to-speech) ─────────────
//
// Katalog auf MODELL-Ebene (Juni-2026-Recherche): ein Provider kann
// mehrere Realtime-Modelle anbieten (OpenAI: Realtime-2 / GA / Mini).
// `key` ist der eindeutige Select-Wert; gespeichert wird weiterhin nur
// `{provider, model, voice}` — das Schema bleibt unverändert.

const OPENAI_REALTIME_VOICES = [
  'marin',
  'cedar',
  'alloy',
  'echo',
  'shimmer',
  'sage',
  'verse',
  'ballad',
];

interface NativeModelOption {
  key: string;
  provider: 'xai' | 'openai';
  model: string;
  label: string;
  voices: string[];
  note: string;
}

const NATIVE_MODEL_OPTIONS: ReadonlyArray<NativeModelOption> = [
  {
    key: 'xai:grok-voice-think-fast-1.0',
    provider: 'xai',
    model: 'grok-voice-think-fast-1.0',
    label: 'xAI · Grok Voice Think Fast',
    voices: ['eve', 'ara', 'rex', 'sal', 'leo'],
    note: 'Empfehlung — $0.05/min flat · #1 τ-voice Tool-Calling · natives μ-law 8 kHz',
  },
  {
    key: 'openai:gpt-realtime-2',
    provider: 'openai',
    model: 'gpt-realtime-2',
    label: 'OpenAI · GPT-Realtime-2',
    voices: OPENAI_REALTIME_VOICES,
    note: 'Neues Flaggschiff (Mai 2026) — GPT-5-Klasse-Reasoning, beste Gesprächsqualität, teuerste Option',
  },
  {
    key: 'openai:gpt-realtime',
    provider: 'openai',
    model: 'gpt-realtime',
    label: 'OpenAI · GPT Realtime (GA)',
    voices: OPENAI_REALTIME_VOICES,
    note: 'Bewährtes GA-Modell — natürliche Sprache, mehrsprachig',
  },
  {
    key: 'openai:gpt-realtime-mini',
    provider: 'openai',
    model: 'gpt-realtime-mini',
    label: 'OpenAI · GPT Realtime Mini',
    voices: OPENAI_REALTIME_VOICES,
    note: 'Günstige OpenAI-Option (~⅓ des GA-Preises) — gute Qualität für einfache Flows',
  },
];

function findNativeOption(
  provider: string | undefined,
  model: string | undefined,
): NativeModelOption {
  return (
    NATIVE_MODEL_OPTIONS.find((o) => o.provider === provider && o.model === model)
    // Fallback: erster Eintrag des Providers (Modell wurde evtl. aus dem
    // Katalog entfernt, aber die Auswahl soll nicht ins Leere zeigen).
    ?? NATIVE_MODEL_OPTIONS.find((o) => o.provider === provider)
    ?? NATIVE_MODEL_OPTIONS[0]!
  );
}

function VoiceAgentModeCard({
  form,
  setForm,
}: {
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
}) {
  const mode = form.voiceAgentMode ?? 'classic';
  const native = form.nativeVoiceAgent;
  const providerOpt = findNativeOption(native?.provider, native?.model);

  function setMode(next: 'classic' | 'native') {
    setForm((p) => ({
      ...p,
      voiceAgentMode: next,
      nativeVoiceAgent:
        next === 'native'
          ? p.nativeVoiceAgent ?? {
              provider: NATIVE_MODEL_OPTIONS[0]!.provider,
              model: NATIVE_MODEL_OPTIONS[0]!.model,
              voice: NATIVE_MODEL_OPTIONS[0]!.voices[0]!,
            }
          : null,
    }));
  }

  function setModelByKey(key: string) {
    const opt = NATIVE_MODEL_OPTIONS.find((o) => o.key === key) ?? NATIVE_MODEL_OPTIONS[0]!;
    setForm((p) => {
      // Stimme nur zurücksetzen wenn der neue Eintrag sie nicht kennt —
      // beim Wechsel GPT-Realtime-2 ↔ Mini bleibt z. B. „marin" erhalten.
      const prevVoice = p.nativeVoiceAgent?.voice;
      const voice =
        prevVoice && opt.voices.includes(prevVoice) ? prevVoice : opt.voices[0]!;
      return {
        ...p,
        nativeVoiceAgent: {
          provider: opt.provider,
          model: opt.model,
          voice,
        },
      };
    });
  }

  function setVoice(v: string) {
    setForm((p) => ({
      ...p,
      nativeVoiceAgent: p.nativeVoiceAgent
        ? { ...p.nativeVoiceAgent, voice: v }
        : { provider: 'xai', model: 'grok-voice-think-fast-1.0', voice: v },
    }));
  }

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardHeader>
        <CardTitle>Voice-Agent-Modus</CardTitle>
        <CardDescription>
          „Klassisch" = Deepgram (STT) → LLM → ElevenLabs (TTS).
          „Nativ" = direktes Speech-to-Speech mit dem Provider — schneller,
          natürlicher, ohne ElevenLabs-Dependency.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            type="button"
            onClick={() => setMode('classic')}
            className={cn(
              'rounded-md border p-3 text-left transition-colors',
              mode === 'classic'
                ? 'border-primary bg-primary/10'
                : 'border-border hover:border-primary/40',
            )}
          >
            <div className="font-medium">Klassisch</div>
            <div className="text-xs text-muted-foreground">
              STT + LLM + TTS getrennt. Stabil, bewährt, Filler-Audio möglich.
            </div>
          </button>
          <button
            type="button"
            onClick={() => setMode('native')}
            className={cn(
              'rounded-md border p-3 text-left transition-colors',
              mode === 'native'
                ? 'border-primary bg-primary/10'
                : 'border-border hover:border-primary/40',
            )}
          >
            <div className="font-medium">Nativ (Speech-to-Speech)</div>
            <div className="text-xs text-muted-foreground">
              Direkter μ-law-Strom zwischen Twilio und Provider. Sub-Sekunden-Latenz.
            </div>
          </button>
        </div>

        {mode === 'native' && (
          <div className="space-y-3 rounded-md border border-border bg-card p-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Modell</Label>
                <Select
                  value={providerOpt.key}
                  onValueChange={(v) => setModelByKey(v)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {NATIVE_MODEL_OPTIONS.map((o) => (
                      <SelectItem key={o.key} value={o.key}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">{providerOpt.note}</p>
              </div>
              <div className="space-y-1.5">
                <Label>Stimme</Label>
                <Select value={native?.voice ?? 'eve'} onValueChange={(v) => setVoice(v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {providerOpt.voices.map((v) => (
                      <SelectItem key={v} value={v}>
                        {v}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Modell-ID: <code className="font-mono text-[10px]">{providerOpt.model}</code>
                </p>
              </div>
            </div>
            <div className="rounded-md bg-amber-500/10 p-2 text-xs text-amber-200">
              Voraussetzung:{' '}
              <strong>
                {native?.provider === 'openai' ? 'OpenAI' : native?.provider === 'google' ? 'Google AI' : 'xAI'}
              </strong>
              -API-Schlüssel im API-Schlüssel-Reiter hinterlegt. Tool-Calling
              (Termine buchen, Tickets erstellen) läuft automatisch durch den
              gewählten Voice-Agent.
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Performance: Latenz + Vorschläge (Ø über alle Anrufe) ───────────────

function PerformanceCard() {
  const report = useEffectiveness(30);
  const data = report.data;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Activity className="size-4 text-primary" />
          Performance &amp; Vorschläge
        </CardTitle>
        <CardDescription className="text-xs">
          Ø-Werte aus den letzten 30 Tagen — hier siehst du, ob Sprache &amp;
          Pipeline aktuell schnell genug antworten und welche Stellschraube
          sich lohnt.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-3">
          <LatencyMetric
            label="Latenz Ø p50"
            value={data?.summary.avgLatencyP50Ms}
            target={700}
            unit="ms"
          />
          <LatencyMetric
            label="Latenz Ø p95"
            value={data?.summary.avgLatencyP95Ms}
            target={1500}
            unit="ms"
          />
          <LatencyMetric
            label="Anrufe ausgewertet"
            value={data?.summary.totalCalls}
            unit=""
            neutral
          />
        </div>
        <SuggestionsList report={data} />
      </CardContent>
    </Card>
  );
}

function LatencyMetric({
  label,
  value,
  target,
  unit,
  neutral,
}: {
  label: string;
  value: number | undefined;
  target?: number;
  unit: string;
  neutral?: boolean;
}) {
  const ok = !neutral && value != null && target != null && value <= target;
  const bad = !neutral && value != null && target != null && value > target * 1.5;
  return (
    <div className="rounded-md border border-border/60 px-3 py-2">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={cn(
          'mt-1 text-xl font-semibold tabular-nums',
          ok && 'text-emerald-300',
          bad && 'text-amber-300',
        )}
      >
        {value == null ? (
          <Skeleton className="h-6 w-14" />
        ) : (
          <>
            {value}
            {unit && <span className="ml-1 text-xs font-normal text-muted-foreground">{unit}</span>}
          </>
        )}
      </div>
      {target != null && !neutral && (
        <div className="mt-0.5 text-[10px] text-muted-foreground">Ziel ≤ {target} {unit}</div>
      )}
    </div>
  );
}

function SuggestionsList({ report }: { report: EffectivenessReport | undefined }) {
  if (!report) {
    return (
      <div className="space-y-1.5">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-2/3" />
      </div>
    );
  }
  const items: string[] = [];
  for (const et of report.perEventType) {
    if (et.avgActualMinutes != null && et.plannedMinutes > 0) {
      const diffPct = ((et.avgActualMinutes - et.plannedMinutes) / et.plannedMinutes) * 100;
      if (diffPct >= 20) {
        items.push(
          `„${et.name}" dauert Ø ${Math.round(diffPct)} % länger — Block-Dauer prüfen.`,
        );
      } else if (diffPct <= -20) {
        items.push(
          `„${et.name}" ist Ø ${Math.round(-diffPct)} % kürzer — engere Slots möglich.`,
        );
      }
    }
  }
  if (report.summary.avgLatencyP50Ms > 1000 && report.summary.totalCalls >= 10) {
    items.push(
      `Latenz p50 = ${report.summary.avgLatencyP50Ms} ms (Ziel < 700 ms). Schnelleres LLM erwägen.`,
    );
  }
  const verifiedRatio =
    report.summary.mirrorEventCount > 0
      ? report.summary.mirrorVerifiedCount / report.summary.mirrorEventCount
      : 1;
  if (verifiedRatio < 0.3 && report.summary.mirrorEventCount >= 5) {
    items.push(
      `Nur ${Math.round(verifiedRatio * 100)} % der Termine sind im Kalender verifiziert.`,
    );
  }
  if (report.summary.ticketsOpen >= 5) {
    items.push(
      `${report.summary.ticketsOpen} offene Tickets — Eskalations-Empfänger prüfen.`,
    );
  }

  if (items.length === 0) {
    return (
      <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
        <Lightbulb className="size-4 text-emerald-400" />
        Keine Auffälligkeiten — die Pipeline läuft im grünen Bereich.
      </p>
    );
  }
  return (
    <ul className="space-y-1.5 text-sm">
      {items.slice(0, 4).map((it, i) => (
        <li key={i} className="flex gap-2">
          <Lightbulb className="size-3.5 shrink-0 translate-y-0.5 text-amber-300" />
          <span className="text-xs leading-snug">{it}</span>
        </li>
      ))}
    </ul>
  );
}
